import React, { useState, useEffect, useMemo, useRef } from 'react';
import { financeiroAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, Search, X, Sparkles, Paperclip, Upload, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ManejoGrid, { manejoZerado } from './ManejoGrid';
import { useLimiteUpload } from '../../lib/uploadLimit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatUploadDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const onlyDigits = (s) => (s || '').replace(/\D/g, '');
const money = (v) => (parseFloat(v) || 0).toFixed(2);

/**
 * Modal de criação/edição de serviço (v6.3).
 * Mudanças:
 *  - Campo "Valor do Serviço (pai)" SEMPRE editável
 *  - Subserviços têm picker para Tipo de Serviço cadastrado (auto-preenche nome+valor)
 *  - Total = valor_servico_principal + soma(itens)
 *  - Layout mostra os 3 totalizadores ao lado das parcelas
 */
export default function ServiceFormModal({ editing, types, methods, categories, clients, onClose, onSaved }) {
  const limiteUpload = useLimiteUpload();
  const [form, setForm] = useState(() => editing ? {
    cliente_id: editing.cliente_id || '',
    tipo_servico_id: editing.tipo_servico_id || '',
    titulo: editing.titulo || '',
    descricao: editing.descricao || '',
    categoria_id: editing.categoria_id || '',
    valor_servico_principal: editing.valor_servico_principal ?? 0,
    forma_pagamento_id: editing.forma_pagamento_id || '',
    parcelas: editing.parcelas || [],
    itens: editing.itens || [],
    data_inicio: editing.data_inicio || new Date().toISOString().slice(0, 10),
    data_conclusao: editing.data_conclusao || '',
    status: editing.status === 'ativo' ? 'em_andamento' : (editing.status || 'em_andamento'),
    observacoes: editing.observacoes || '',
    gerir_manejo: !!editing.gerir_manejo,
    manejo: editing.manejo || manejoZerado(),
  } : {
    cliente_id: '',
    tipo_servico_id: '',
    titulo: '',
    descricao: '',
    categoria_id: '',
    valor_servico_principal: 0,
    forma_pagamento_id: '',
    parcelas: [],
    itens: [],
    data_inicio: new Date().toISOString().slice(0, 10),
    data_conclusao: '',
    status: 'em_andamento',
    observacoes: '',
    gerir_manejo: false,
    manejo: manejoZerado(),
  });
  const [saving, setSaving] = useState(false);
  const [numParcelas, setNumParcelas] = useState(1);
  const [anexos, setAnexos] = useState(() => (editing?.anexos || []));
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const clienteSelecionado = clients.find(c => c.id === form.cliente_id);

  // Totais calculados em tempo real
  const subtotalItens = useMemo(
    () => form.itens.reduce((s, it) => s + (parseFloat(it.valor) || 0), 0),
    [form.itens]
  );
  const valorTotal = useMemo(
    () => +((parseFloat(form.valor_servico_principal) || 0) + subtotalItens).toFixed(2),
    [form.valor_servico_principal, subtotalItens]
  );

  // Tipo de serviço selecionado no PAI → auto-preenche TÍTULO + DESCRIÇÃO + VALOR PRINCIPAL
  const handleTipoChange = (v) => {
    if (v === 'none') {
      setForm(f => ({ ...f, tipo_servico_id: '' }));
      return;
    }
    const t = types.find(x => x.id === v);
    setForm(f => ({
      ...f,
      tipo_servico_id: v,
      descricao: f.descricao && f.descricao.trim() ? f.descricao : (t?.descricao || ''),
      valor_servico_principal: (!f.valor_servico_principal || f.valor_servico_principal === 0) ? (t?.valor_padrao || 0) : f.valor_servico_principal,
      titulo: f.titulo && f.titulo.trim() ? f.titulo : (t?.nome || ''),
    }));
  };

  // Subserviços
  const addItemManual = () => {
    setForm(f => ({ ...f, itens: [...f.itens, { id: 'new-' + Date.now(), descricao: '', valor: 0, tipo_servico_id: null }] }));
  };
  const addItemFromTipo = (tipoId) => {
    const t = types.find(x => x.id === tipoId);
    if (!t) return;
    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        id: 'new-' + Date.now(),
        descricao: t.nome + (t.descricao ? ' — ' + t.descricao : ''),
        valor: t.valor_padrao || 0,
        tipo_servico_id: tipoId,
      }],
    }));
  };
  const removeItem = (idx) => {
    setForm(f => ({ ...f, itens: f.itens.filter((_, i) => i !== idx) }));
  };
  const updateItem = (idx, field, value) => {
    setForm(f => ({
      ...f,
      itens: f.itens.map((it, i) => i === idx ? { ...it, [field]: field === 'valor' ? (parseFloat(value) || 0) : value } : it),
    }));
  };

  // Gerar parcelas usa o TOTAL (principal + itens)
  const gerarParcelas = () => {
    const n = parseInt(numParcelas) || 1;
    const valor = valorTotal;
    const valorParcela = +(valor / n).toFixed(2);
    const hoje = new Date(form.data_inicio || new Date());
    const parcelas = Array.from({ length: n }, (_, i) => {
      const venc = new Date(hoje);
      venc.setMonth(venc.getMonth() + i);
      return {
        numero: i + 1,
        valor: i === n - 1 ? +(valor - valorParcela * (n - 1)).toFixed(2) : valorParcela,
        vencimento: venc.toISOString().slice(0, 10),
        pago: false,
        pago_em: null,
        forma_pagamento_id: form.forma_pagamento_id || null,
      };
    });
    setForm(f => ({ ...f, parcelas }));
  };

  // ===== Anexos (Documentos) =====
  const handleFilesSelected = async (fileList) => {
    if (!editing?.id) {
      toast.error('Salve o serviço primeiro para anexar documentos');
      return;
    }
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.size > limiteUpload.bytes) {
        toast.error(`"${file.name}" excede ${limiteUpload.mb} MB`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await financeiroAPI.uploadAttachment(editing.id, fd);
        setAnexos((prev) => [...prev, data]);
        toast.success(`"${file.name}" enviado`);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Erro ao enviar "${file.name}"`);
      } finally {
        setUploading(false);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAnexo = async (attId, name) => {
    if (!window.confirm(`Excluir "${name}"?`)) return;
    try {
      await financeiroAPI.deleteAttachment(editing.id, attId);
      setAnexos((prev) => prev.filter((a) => a.id !== attId));
      toast.success('Documento excluído');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleDownloadAnexo = (attId) => {
    const token = localStorage.getItem('agrolink_token');
    const url = `${BACKEND_URL}/api/financeiro/services/${editing.id}/attachments/${attId}?token=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank');
  };

  const handleSubmit = async () => {
    if (!form.cliente_id || !form.titulo) {
      toast.error('Cliente e título são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor_servico_principal: parseFloat(form.valor_servico_principal) || 0,
        valor_total: valorTotal,
        itens: form.itens.map(({ id, ...rest }) => ({
          descricao: (rest.descricao || '').trim(),
          valor: parseFloat(rest.valor) || 0,
          tipo_servico_id: rest.tipo_servico_id || null,
        })),
        gerir_manejo: !!form.gerir_manejo,
        manejo: form.gerir_manejo ? form.manejo : manejoZerado(),
      };
      if (editing) {
        await financeiroAPI.updateService(editing.id, payload);
        toast.success('Serviço atualizado');
      } else {
        await financeiroAPI.createService(payload);
        toast.success('Serviço criado');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-servico-form">
        <DialogHeader><DialogTitle>{editing ? 'Editar Serviço' : 'Novo Serviço'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* ===== Cliente (busca) ===== */}
          <ClienteAutocomplete
            clients={clients}
            value={form.cliente_id}
            onChange={(id) => setForm(f => ({ ...f, cliente_id: id }))}
            selecionado={clienteSelecionado}
          />

          {/* ===== Serviço Pai ===== */}
          <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/30">
            <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-3">Serviço Principal (Pai)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Tipo de Serviço</Label>
                <Select value={form.tipo_servico_id || 'none'} onValueChange={handleTipoChange}>
                  <SelectTrigger data-testid="form-tipo"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nenhum —</SelectItem>
                    {types.filter(t => t.ativo).map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Auto-preenche título, descrição e valor padrão</p>
              </div>
              <div>
                <Label>Título *</Label>
                <Input value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} data-testid="form-titulo" />
              </div>
            </div>
            <div className="mt-3">
              <Label>Descrição</Label>
              <Textarea rows={2} value={form.descricao} onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))} />
            </div>
            <div className="mt-3">
              <Label>Valor do Serviço Principal (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.valor_servico_principal}
                onChange={(e) => setForm(f => ({ ...f, valor_servico_principal: e.target.value }))}
                data-testid="form-valor-principal"
                className="text-lg font-semibold"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Valor base do serviço (será somado com os subserviços abaixo)</p>
            </div>
          </div>

          {/* ===== Subserviços (itens) ===== */}
          <div className="border rounded-lg p-3 bg-cyan-50/30">
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
              <div>
                <p className="font-semibold text-sm">Subserviços (Adicionais)</p>
                <p className="text-[11px] text-muted-foreground">Adicione itens cadastrados ou personalizados. Cada um SOMA ao total.</p>
              </div>
              <div className="flex gap-2">
                <PickTipoServico types={types} onPick={addItemFromTipo} />
                <Button type="button" size="sm" variant="outline" onClick={addItemManual} data-testid="btn-add-item-manual">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Manual
                </Button>
              </div>
            </div>
            {form.itens.length === 0 ? (
              <p className="text-xs text-center text-muted-foreground py-3 italic">Nenhum subserviço adicionado.</p>
            ) : (
              <div className="space-y-2">
                {form.itens.map((it, idx) => (
                  <div key={it.id || idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-8"
                      placeholder="Descrição"
                      value={it.descricao}
                      onChange={(e) => updateItem(idx, 'descricao', e.target.value)}
                      data-testid={`item-desc-${idx}`}
                    />
                    <Input
                      className="col-span-3"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={it.valor}
                      onChange={(e) => updateItem(idx, 'valor', e.target.value)}
                      data-testid={`item-valor-${idx}`}
                    />
                    <button type="button" className="col-span-1 text-rose-600 flex justify-center" onClick={() => removeItem(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-2 border-t mt-2 text-sm">
                  <p>Subtotal subserviços: <span className="font-semibold">R$ {money(subtotalItens)}</span></p>
                </div>
              </div>
            )}
          </div>

          {/* ===== Totalizador ===== */}
          <div className="grid grid-cols-3 gap-2 p-3 bg-gradient-to-r from-emerald-50 to-cyan-50 border-2 border-emerald-200 rounded-lg">
            <div className="text-center">
              <p className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">Principal</p>
              <p className="text-lg font-bold text-emerald-700">R$ {money(form.valor_servico_principal)}</p>
            </div>
            <div className="text-center border-x">
              <p className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">+ Subserviços</p>
              <p className="text-lg font-bold text-cyan-700">R$ {money(subtotalItens)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">= Total</p>
              <p className="text-xl font-bold text-emerald-800">R$ {money(valorTotal)}</p>
            </div>
          </div>

          {/* ===== Categoria + Forma Pgto ===== */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={form.categoria_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, categoria_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhuma —</SelectItem>
                  {categories.filter(c => c.ativo && c.tipo === 'receita').map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma de Pagamento</Label>
              <Select value={form.forma_pagamento_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, forma_pagamento_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhuma —</SelectItem>
                  {methods.filter(m => m.ativo).map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ===== Datas & Status ===== */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Data Início</Label>
              <Input type="date" value={form.data_inicio} onChange={(e) => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
            </div>
            <div>
              <Label>Data Conclusão</Label>
              <Input type="date" value={form.data_conclusao} onChange={(e) => setForm(f => ({ ...f, data_conclusao: e.target.value }))} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="aguardando_cliente">Aguardando Cliente</SelectItem>
                  <SelectItem value="aguardando_pagamento">Aguardando Pagamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ===== Parcelas ===== */}
          <div className="border rounded-lg p-3 bg-muted/30">
            <div className="flex items-end gap-2 mb-3">
              <div>
                <Label>Nº de Parcelas</Label>
                <Input type="number" min="1" max="60" value={numParcelas} onChange={(e) => setNumParcelas(e.target.value)} className="w-24" />
              </div>
              <Button type="button" variant="outline" onClick={gerarParcelas} data-testid="btn-gerar-parcelas">Gerar Parcelas</Button>
              <p className="text-[11px] text-muted-foreground self-center ml-2">Recalcula do TOTAL atual (R$ {money(valorTotal)})</p>
            </div>
            {form.parcelas.length > 0 && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {form.parcelas.map((p, idx) => (
                  <div key={p.id || idx} className="grid grid-cols-12 gap-2 items-center text-sm">
                    <span className="col-span-1 text-center font-semibold">#{p.numero}</span>
                    <Input className="col-span-4" type="number" step="0.01" value={p.valor} onChange={(e) => {
                      const v = e.target.value;
                      setForm(f => ({ ...f, parcelas: f.parcelas.map((x, i) => i === idx ? { ...x, valor: parseFloat(v) || 0 } : x) }));
                    }} />
                    <Input className="col-span-4" type="date" value={p.vencimento} onChange={(e) => {
                      const v = e.target.value;
                      setForm(f => ({ ...f, parcelas: f.parcelas.map((x, i) => i === idx ? { ...x, vencimento: v } : x) }));
                    }} />
                    <label className="col-span-2 flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={p.pago} onChange={(e) => {
                        const c = e.target.checked;
                        setForm(f => ({ ...f, parcelas: f.parcelas.map((x, i) => i === idx ? { ...x, pago: c, pago_em: c ? new Date().toISOString() : null } : x) }));
                      }} /> Pago
                    </label>
                    <button type="button" className="col-span-1 text-rose-600 flex justify-center" onClick={() => setForm(f => ({ ...f, parcelas: f.parcelas.filter((_, i) => i !== idx).map((x, i) => ({ ...x, numero: i + 1 })) }))}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ===== Gerir Manejo ===== */}
          <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50/30">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.gerir_manejo}
                onChange={(e) => setForm(f => ({ ...f, gerir_manejo: e.target.checked }))}
                className="w-5 h-5 rounded text-amber-600 focus:ring-amber-500"
                data-testid="checkbox-gerir-manejo"
              />
              <div>
                <p className="font-semibold text-sm text-amber-900">Gerir Manejo (animais)</p>
                <p className="text-[11px] text-amber-700">Habilita a gestão de animais por sexo e idade dentro deste serviço (igual sistema IAGRO)</p>
              </div>
            </label>
            {form.gerir_manejo && (
              <div className="mt-3">
                <ManejoGrid
                  value={form.manejo}
                  onChange={(m) => setForm(f => ({ ...f, manejo: m }))}
                />
              </div>
            )}
          </div>

          {/* ===== Documentos (Anexos) — apenas em edição ===== */}
          {editing?.id && (
            <div className="border-2 border-indigo-200 rounded-lg p-4 bg-indigo-50/30" data-testid="section-anexos">
              <div className="flex items-center gap-2 mb-3">
                <Paperclip className="w-4 h-4 text-indigo-700" />
                <p className="font-semibold text-sm text-indigo-900">Documentos</p>
                <span className="text-[11px] text-indigo-600">{anexos.length} arquivo(s) · até {limiteUpload.mb} MB cada</span>
              </div>

              {/* Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFilesSelected(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-indigo-500 bg-indigo-100/60' : 'border-indigo-300 bg-white/50 hover:bg-indigo-100/40'
                } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                data-testid="anexos-dropzone"
              >
                <Upload className="w-6 h-6 mx-auto mb-1 text-indigo-500" />
                <p className="text-xs font-medium text-indigo-800">
                  {uploading ? 'Enviando...' : 'Arraste arquivos aqui ou clique para selecionar'}
                </p>
                <p className="text-[10px] text-indigo-600 mt-0.5">PDF, imagens, XLSX, DOCX etc. — máx {limiteUpload.mb} MB por arquivo</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  data-testid="anexos-input"
                />
              </div>

              {/* Lista */}
              {anexos.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="anexos-list">
                  {anexos.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 p-2 bg-white border rounded-md hover:border-indigo-300 transition-colors"
                      data-testid={`anexo-item-${a.id}`}
                    >
                      <FileText className="w-4 h-4 text-indigo-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" title={a.original_name || a.filename}>
                          {a.original_name || a.filename}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatFileSize(a.size)}
                          {a.uploaded_by ? ` · ${a.uploaded_by}` : ''}
                          {a.uploaded_at ? ` · ${formatUploadDate(a.uploaded_at)}` : ''}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadAnexo(a.id)}
                        title="Baixar"
                        data-testid={`anexo-download-${a.id}`}
                      >
                        <Download className="w-4 h-4 text-indigo-600" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteAnexo(a.id, a.original_name || a.filename)}
                        title="Excluir"
                        data-testid={`anexo-delete-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label>Observações</Label>
            <Textarea rows={2} value={form.observacoes} onChange={(e) => setForm(f => ({ ...f, observacoes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="btn-save-servico">
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Picker rápido para escolher Tipo de Serviço como subserviço
   ============================================================ */
function PickTipoServico({ types, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const ativos = types.filter(t => t.ativo);

  return (
    <div ref={ref} className="relative">
      <Button type="button" size="sm" onClick={() => setOpen(o => !o)} data-testid="btn-add-item-cadastrado">
        <Sparkles className="w-3.5 h-3.5 mr-1" /> Cadastrado
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 max-h-72 overflow-y-auto bg-card border rounded-lg shadow-lg z-50" data-testid="pick-tipo-list">
          {ativos.length === 0 ? (
            <p className="text-xs text-muted-foreground p-3 text-center">Nenhum tipo cadastrado. Crie em Configurações → Tipos de Serviço.</p>
          ) : (
            ativos.map(t => (
              <button
                key={t.id}
                type="button"
                className="w-full text-left p-2.5 hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                onClick={() => { onPick(t.id); setOpen(false); }}
                data-testid={`pick-tipo-${t.id}`}
              >
                <div className="flex justify-between items-center">
                  <p className="font-medium text-sm">{t.nome}</p>
                  <p className="text-xs text-emerald-600 font-semibold">R$ {money(t.valor_padrao)}</p>
                </div>
                {t.descricao && <p className="text-[11px] text-muted-foreground truncate">{t.descricao}</p>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Autocomplete de Cliente (busca por nome ou CPF)
   ============================================================ */
function ClienteAutocomplete({ clients, value, onChange, selecionado }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    if (!query) return clients.slice(0, 10);
    const q = query.toLowerCase();
    const qDigits = onlyDigits(query);
    return clients
      .filter(c => {
        const nome = (c.nome_completo || '').toLowerCase();
        const cpf = onlyDigits(c.cpf || '');
        const tel = onlyDigits(c.telefone || '');
        return nome.includes(q) || (qDigits && (cpf.includes(qDigits) || tel.includes(qDigits)));
      })
      .slice(0, 15);
  }, [query, clients]);

  if (selecionado && !open) {
    return (
      <div>
        <Label>Cliente *</Label>
        <div className="flex items-center gap-2 p-2.5 border rounded-lg bg-emerald-50/50 border-emerald-200" data-testid="cliente-selecionado">
          <div className="flex-1 min-w-0">
            <p className="font-medium">{selecionado.nome_completo}</p>
            <p className="text-xs text-muted-foreground">
              {selecionado.cpf || 'sem CPF'}
              {selecionado.telefone ? ` · ${selecionado.telefone}` : ''}
              {selecionado.email ? ` · ${selecionado.email}` : ''}
            </p>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => { onChange(''); setQuery(''); setOpen(true); }} data-testid="btn-trocar-cliente">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Label>Cliente *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-10"
          placeholder="Buscar por nome, CPF ou telefone..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid="cliente-search-input"
        />
      </div>
      {open && (
        <div className="border rounded-lg mt-1 max-h-56 overflow-y-auto bg-card shadow-lg z-50 relative" data-testid="cliente-search-results">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhum cliente encontrado</p>
          ) : (
            filtered.map(c => (
              <button
                key={c.id}
                type="button"
                className="w-full text-left p-2.5 hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                data-testid={`cliente-option-${c.id}`}
              >
                <p className="font-medium text-sm">{c.nome_completo}</p>
                <p className="text-xs text-muted-foreground">
                  {c.cpf || 'sem CPF'}
                  {c.telefone ? ` · ${c.telefone}` : ''}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
